import { Assertion, AssertionOp } from '../../types';
import { Button } from '../common/Button';
import { Input, Select, checkboxClass } from '../common/Input';

const KINDS: { value: Assertion['type']; label: string }[] = [
  { value: 'status', label: 'Status code' },
  { value: 'body_contains', label: 'Body contains' },
  { value: 'json_path', label: 'JSON path' },
  { value: 'header', label: 'Response header' },
  { value: 'latency', label: 'Latency bound' },
];

const JSON_OPS: { value: AssertionOp; label: string }[] = [
  { value: 'eq', label: '=' }, { value: 'ne', label: '≠' }, { value: 'gt', label: '>' }, { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' }, { value: 'lte', label: '≤' }, { value: 'contains', label: 'contains' }, { value: 'exists', label: 'exists' },
];

const HEADER_OPS: { value: 'eq' | 'contains' | 'exists'; label: string }[] = [
  { value: 'eq', label: '=' }, { value: 'contains', label: 'contains' }, { value: 'exists', label: 'exists' },
];

export function blankAssertion(type: Assertion['type']): Assertion {
  switch (type) {
    case 'status': return { type, value: '2xx' };
    case 'body_contains': return { type, value: '' };
    case 'json_path': return { type, path: '$.status', op: 'eq', value: 'ok' };
    case 'header': return { type, name: 'Content-Type', op: 'contains', value: 'json' };
    case 'latency': return { type, max_ms: 1000 };
  }
}

/** Words for the settings tab and the diagnostics panel. */
export function describeAssertion(a: Assertion): string {
  switch (a.type) {
    case 'status': return `Status ${a.value}`;
    case 'body_contains': return `Body ${a.negate ? 'does not contain' : 'contains'} "${a.value}"`;
    case 'json_path': return `${a.path} ${JSON_OPS.find(o => o.value === a.op)?.label ?? a.op}${a.op === 'exists' ? '' : ` ${JSON.stringify(a.value)}`}`;
    case 'header': return `Header ${a.name} ${a.op === 'exists' ? 'exists' : `${a.op === 'eq' ? '=' : 'contains'} "${a.value ?? ''}"`}`;
    case 'latency': return `Responds within ${a.max_ms} ms`;
  }
}

interface Props {
  value: Assertion[];
  onChange: (next: Assertion[]) => void;
}

/**
 * A list of expectations about the response. Every row is one assertion; the
 * service is up only when all of them pass. A latency bound that fails
 * degrades rather than downs — it is a speed judgement, not a failure.
 */
export function AssertionsEditor({ value, onChange }: Props) {
  const set = (i: number, next: Assertion) => onChange(value.map((a, j) => (j === i ? next : a)));
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  const add = () => onChange([...value, blankAssertion(value.some(a => a.type === 'status') ? 'body_contains' : 'status')]);

  const row = 'w-full px-2 py-1.5 rounded-md border text-sm bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500';

  return (
    <div className="space-y-2">
      {value.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No assertions: any response counts as up. Add at least a status code.
        </p>
      )}
      {value.map((a, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`assertion-${i}`}>
          <select
            aria-label={`Assertion ${i + 1} type`}
            className={`${row} w-auto`}
            value={a.type}
            onChange={e => set(i, blankAssertion(e.target.value as Assertion['type']))}
          >
            {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>

          {a.type === 'status' && (
            <Input
              aria-label={`Assertion ${i + 1} status`}
              className="!w-40 font-mono"
              value={a.value}
              placeholder="200, 2xx, 200-299"
              onChange={e => set(i, { ...a, value: e.target.value })}
            />
          )}

          {a.type === 'body_contains' && (
            <>
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={Boolean(a.negate)}
                  onChange={e => set(i, { ...a, negate: e.target.checked })}
                />
                not
              </label>
              <Input
                aria-label={`Assertion ${i + 1} text`}
                className="!w-64"
                value={a.value}
                placeholder="text the body must contain"
                onChange={e => set(i, { ...a, value: e.target.value })}
              />
            </>
          )}

          {a.type === 'json_path' && (
            <>
              <Input
                aria-label={`Assertion ${i + 1} path`}
                className="!w-44 font-mono"
                value={a.path}
                placeholder="$.status"
                onChange={e => set(i, { ...a, path: e.target.value })}
              />
              <Select
                aria-label={`Assertion ${i + 1} operator`}
                className="!w-auto"
                value={a.op}
                onChange={e => set(i, { ...a, op: e.target.value as AssertionOp })}
              >
                {JSON_OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
              {a.op !== 'exists' && (
                <Input
                  aria-label={`Assertion ${i + 1} value`}
                  className="!w-40 font-mono"
                  value={a.value === null || a.value === undefined ? '' : String(a.value)}
                  placeholder="ok"
                  onChange={e => set(i, { ...a, value: coerce(e.target.value) })}
                />
              )}
            </>
          )}

          {a.type === 'header' && (
            <>
              <Input
                aria-label={`Assertion ${i + 1} header name`}
                className="!w-44 font-mono"
                value={a.name}
                placeholder="Content-Type"
                onChange={e => set(i, { ...a, name: e.target.value })}
              />
              <Select
                aria-label={`Assertion ${i + 1} operator`}
                className="!w-auto"
                value={a.op}
                onChange={e => set(i, { ...a, op: e.target.value as 'eq' | 'contains' | 'exists' })}
              >
                {HEADER_OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
              {a.op !== 'exists' && (
                <Input
                  aria-label={`Assertion ${i + 1} value`}
                  className="!w-40"
                  value={a.value ?? ''}
                  onChange={e => set(i, { ...a, value: e.target.value })}
                />
              )}
            </>
          )}

          {a.type === 'latency' && (
            <>
              <Input
                aria-label={`Assertion ${i + 1} milliseconds`}
                type="number"
                min={1}
                className="!w-28 font-mono"
                value={a.max_ms}
                onChange={e => set(i, { ...a, max_ms: Math.max(1, parseInt(e.target.value, 10) || 1) })}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">ms (fails as degraded)</span>
            </>
          )}

          <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)} aria-label={`Remove assertion ${i + 1}`}>
            Remove
          </Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="secondary" onClick={add}>Add assertion</Button>
    </div>
  );
}

/** Numbers and booleans typed into the value box compare as such; everything else is text. */
function coerce(raw: string): string | number | boolean | null {
  const t = raw.trim();
  if (t === 'true') return true;
  if (t === 'false') return false;
  if (t === 'null') return null;
  if (t !== '' && Number.isFinite(Number(t)) && !/^0\d/.test(t)) return Number(t);
  return raw;
}
